import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'hijack_lobbyFilters';

export type LobbyFormat = 'cash' | 'sitgo' | 'tournament';

/**
 * The Sit & Go ladder's seat toggle (EN-10747): Heads-up and Six-seat, each
 * toggled on or off by itself (EN-11104) — both on lists both formats, both
 * off lists none. Read by the Sit & Go tab's own pills (SITGO_SEAT_CHIPS) and
 * the drawer's Sit & Go Table Size while the `sitgoSeatToggle` flag is on;
 * with it off nothing reads this.
 */
export type SitgoSeats = 'headsUp' | 'sixSeat';

/** Both formats on, so a new player sees every Sit & Go (EN-11104). */
export const DEFAULT_SITGO_SEATS: readonly SitgoSeats[] = Object.freeze(['headsUp', 'sixSeat']);

/**
 * How many seats each format IS. Production runs exactly these two Sit & Go
 * products — a six-seat ladder, and the heads-up ladder priced off it — so a
 * chip shows the seat count it names and nothing else. One place to change if
 * a third format ever ships.
 */
export const SEATS_BY_FORMAT: Readonly<Record<SitgoSeats, number>> = Object.freeze({
  headsUp: 2,
  sixSeat: 6
});

const isSitgoSeats = (v: unknown): v is SitgoSeats => v === 'headsUp' || v === 'sixSeat';

// A list of known formats is the player's choice, an empty one included. The
// radio build stored a single format, but the radio made the player pick one,
// so it is not a choice to hide the other and reads as the default (both on),
// like junk does.
const readSitgoSeats = (v: unknown): SitgoSeats[] => {
  if (Array.isArray(v) && v.every(isSitgoSeats)) return Array.from(new Set(v));
  return [...DEFAULT_SITGO_SEATS];
};

// NOTE: the lobby tile-size preference is NOT here anymore — it moved out of the
// Table Filters drawer into Account → Settings → Lobby Settings. It now lives in
// a shared store (see lobbyCardSize.ts) so the settings panel and the live lobby
// stay in sync.

/** One tab's Table Filters drawer picks. Each tab keeps its own. */
export interface DrawerSelections {
  // Selected blind-level ids (see blindLevels.ts). Empty = no blind filter.
  // Only the Cash drawer offers blinds.
  blinds: string[];
  // Table Size ids. Empty = no size filter. Cash and Sit & Go (flag off) only.
  sizes: string[];
}

export interface PersistedLobbyFilters {
  chipSelections: Record<LobbyFormat, string[]>;
  sitgoSeats: SitgoSeats[];
  // Per tab, so a pick on one tab never filters another (EN-10747). The
  // tournament drawer has no blinds or sizes; its entry stays empty.
  drawerByFormat: Record<LobbyFormat, DrawerSelections>;
}

const noDrawerPicks = (): DrawerSelections => ({ blinds: [], sizes: [] });

const DEFAULT_FILTERS: PersistedLobbyFilters = {
  chipSelections: {
    cash: ['holdem', 'omaha', 'hilo'],
    // Sit & Go is Hold'em only (no Omaha sit & gos).
    sitgo: ['holdem'],
    // No hideEmptyDays chip: empty days are never shown and there is no setting
    // for it (owner, 2026-09-10 — see TournamentDayCarousel). A stale
    // 'hideEmptyDays' left in a player's stored list is simply never read.
    tournament: ['reentry', 'bounty', 'freezeout', 'freeroll', 'satellite']
  },
  sitgoSeats: [...DEFAULT_SITGO_SEATS],
  drawerByFormat: {
    cash: noDrawerPicks(),
    sitgo: noDrawerPicks(),
    tournament: noDrawerPicks()
  }
};

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

// Every drawer list falls back to empty unless storage holds a real array of
// strings — a hand-edited or half-written value never reaches the filters.
const readDrawerSelections = (v: any): DrawerSelections => ({
  blinds: isStringArray(v?.blinds) ? v.blinds : [],
  sizes: isStringArray(v?.sizes) ? v.sizes : []
});

const readDrawerByFormat = (parsed: any): Record<LobbyFormat, DrawerSelections> => {
  const byFormat = parsed.drawerByFormat;
  if (byFormat && typeof byFormat === 'object') {
    return {
      cash: readDrawerSelections(byFormat.cash),
      sitgo: readDrawerSelections(byFormat.sitgo),
      tournament: readDrawerSelections(byFormat.tournament)
    };
  }
  // A store from before EN-10747 holds one `drawer` that every tab shared.
  // Blinds were only ever offered on Cash, and its sizes were filtering Cash,
  // so Cash keeps them and looks as it did. Sit & Go starts clear: carrying a
  // cash "9 Max" over would empty it — the very leak this split removes — and
  // a clear drawer can only show more sit & gos, never fewer.
  return {
    cash: readDrawerSelections(parsed.drawer),
    sitgo: noDrawerPicks(),
    tournament: noDrawerPicks()
  };
};

const readFilters = (): PersistedLobbyFilters => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw);
    const chips = parsed.chipSelections ?? {};
    return {
      chipSelections: {
        cash: isStringArray(chips.cash) ? chips.cash : DEFAULT_FILTERS.chipSelections.cash,
        sitgo: isStringArray(chips.sitgo) ? chips.sitgo : DEFAULT_FILTERS.chipSelections.sitgo,
        tournament: isStringArray(chips.tournament)
          ? chips.tournament
          : DEFAULT_FILTERS.chipSelections.tournament
      },
      sitgoSeats: readSitgoSeats(parsed.sitgoSeats),
      drawerByFormat: readDrawerByFormat(parsed)
    };
  } catch {
    return DEFAULT_FILTERS;
  }
};

const writeFilters = (filters: PersistedLobbyFilters) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    /* localStorage may be unavailable (private mode, etc.) — settings stay in memory */
  }
};

/**
 * Turn one Sit & Go format on in the stored seat toggle from outside the
 * lobby, for a caller that sends the player there while it is unmounted
 * (EN-11109: leaving a finished Sit & Go shows the format just played). The
 * other format is left as it was. useLobbyFilters reads the store on mount, so
 * the lobby opens on it. Like the toggle itself, the choice is device-global,
 * not per player.
 */
export const showPersistedSitgoSeats = (seats: SitgoSeats): void => {
  const stored = readFilters();
  if (stored.sitgoSeats.includes(seats)) return;
  writeFilters({ ...stored, sitgoSeats: [...stored.sitgoSeats, seats] });
};

export const useLobbyFilters = () => {
  const [filters, setFilters] = useState<PersistedLobbyFilters>(() => readFilters());

  useEffect(() => {
    writeFilters(filters);
  }, [filters]);

  const toggleChip = useCallback((format: LobbyFormat, id: string) => {
    setFilters((prev) => {
      const current = prev.chipSelections[format];
      const next = current.includes(id) ? current.filter((c) => c !== id) : [...current, id];
      return {
        ...prev,
        chipSelections: { ...prev.chipSelections, [format]: next }
      };
    });
  }, []);

  // Replaces one tab's drawer picks; every other tab's are left as they are.
  const setDrawer = useCallback((format: LobbyFormat, next: DrawerSelections) => {
    setFilters((prev) => ({
      ...prev,
      drawerByFormat: { ...prev.drawerByFormat, [format]: next }
    }));
  }, []);

  // Each format toggles by itself, so both can be on, or both off.
  const toggleSitgoSeats = useCallback((seats: SitgoSeats) => {
    setFilters((prev) => ({
      ...prev,
      sitgoSeats: prev.sitgoSeats.includes(seats)
        ? prev.sitgoSeats.filter((s) => s !== seats)
        : [...prev.sitgoSeats, seats]
    }));
  }, []);

  // One format on and the other off, for a surface that sends the player to
  // just that format (EN-10748: FIND A HEADS-UP TABLE lights Heads-up only).
  const showOnlySitgoSeats = useCallback((seats: SitgoSeats) => {
    setFilters((prev) => ({ ...prev, sitgoSeats: [seats] }));
  }, []);

  // Restore a single format to its shipped defaults: re-select every game-type
  // chip and clear the hide-full/hide-empty chips — neither of which is in the
  // default selection — plus wipe that tab's drawer blind/size picks. The other
  // formats' chips and drawer picks are left untouched. Powers the drawer's
  // "Clear to default" button.
  const resetToDefault = useCallback((format: LobbyFormat) => {
    setFilters((prev) => ({
      ...prev,
      chipSelections: {
        ...prev.chipSelections,
        [format]: [...DEFAULT_FILTERS.chipSelections[format]]
      },
      // The seat toggle is a Sit & Go setting, so it resets with that format only.
      sitgoSeats: format === 'sitgo' ? [...DEFAULT_SITGO_SEATS] : prev.sitgoSeats,
      drawerByFormat: { ...prev.drawerByFormat, [format]: noDrawerPicks() }
    }));
  }, []);

  return {
    filters,
    toggleChip,
    setDrawer,
    resetToDefault,
    toggleSitgoSeats,
    showOnlySitgoSeats
  };
};

export { DEFAULT_FILTERS };
